import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { i18n } from '@/main'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0, behavior: 'smooth' }
    }
  },
  routes: [
    {
      path: '/',
      redirect: '/zh'
    },
    {
      path: '/:lang',
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('@/views/Home.vue'),
          meta: { title: '首页' }
        },   
        {
          path: 'about',
          name: 'about',
          component: () => import('@/views/About.vue'),
          meta: { title: '关于我们' }
        },
        {
          path: 'services',
          name: 'services',
          component: () => import('@/views/Services.vue'),
          meta: { title: '业务范围' }
        },
        {
          path: 'projects',
          name: 'projects',
          component: () => import('@/views/Projects.vue'),
          meta: { title: '项目案例' }
        },
        {
          path: 'projects/:id',
          name: 'project-detail',
          component: () => import('@/views/ProjectDetail.vue'),
          meta: { title: '项目详情' }
        },
        {
          path: 'news',
          name: 'news',
          component: () => import('@/views/News.vue'),
          meta: { title: '新闻资讯' }
        },
        {
          path: 'news/:id',
          name: 'news-detail',
          component: () => import('@/views/NewsDetail.vue'),
          meta: { title: '新闻详情' }
        },
        {
          path: 'gallery',
          name: 'gallery',
          component: () => import('@/views/Gallery.vue'),
          meta: { title: '图片视频' }
        },
        {
          path: 'contact',
          name: 'contact',
          component: () => import('@/views/Contact.vue'),
          meta: { title: '联系我们' }
        },
        {
          path: 'achievements',
          name: 'achievements',
          component: () => import('@/views/Achievements.vue'),
          meta: { title: '项目业绩表' }
        }
      ]
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/views/admin/Dashboard.vue'),
      meta: { title: '管理后台', requiresAuth: true },
      children: [
        {
          path: '',
          name: 'admin-dashboard',
          component: () => import('@/views/admin/DashboardHome.vue'),
          meta: { title: '仪表盘' }
        },
        {
          path: 'articles',
          name: 'admin-articles',
          component: () => import('@/views/admin/Articles.vue'),
          meta: { title: '文章管理' }
        },
        {
          path: 'articles/new',
          name: 'admin-article-new',
          component: () => import('@/views/admin/ArticleEdit.vue'),
          meta: { title: '新建文章' }
        },
        {
          path: 'articles/:id/edit',
          name: 'admin-article-edit',
          component: () => import('@/views/admin/ArticleEdit.vue'),
          meta: { title: '编辑文章' }
        },
        {
          path: 'media',
          name: 'admin-media',
          component: () => import('@/views/admin/Media.vue'),
          meta: { title: '媒体管理' }
        },
        {
          path: 'projects',
          name: 'admin-projects',
          component: () => import('@/views/admin/ProjectsAdmin.vue'),
          meta: { title: '项目管理' }
        },
        {
          path: 'projects/new',
          name: 'admin-project-new',
          component: () => import('@/views/admin/ProjectEdit.vue'),
          meta: { title: '新建项目' }
        },
        {
          path: 'projects/:id/edit',
          name: 'admin-project-edit',
          component: () => import('@/views/admin/ProjectEdit.vue'),
          meta: { title: '编辑项目' }
        },
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('@/views/admin/Users.vue'),
          meta: { title: '用户管理' }
        },
        {
          path: 'users/new',
          name: 'admin-user-new',
          component: () => import('@/views/admin/UserEdit.vue'),
          meta: { title: '新建用户' }
        },
        {
          path: 'users/:id/edit',
          name: 'admin-user-edit',
          component: () => import('@/views/admin/UserEdit.vue'),
          meta: { title: '编辑用户' }
        },
        {
          path: 'user-permissions',
          name: 'admin-user-permissions',
          component: () => import('@/views/admin/UserPermissions.vue'),
          meta: { title: '用户权限管理' }
        },
        {
          path: 'settings',
          name: 'admin-settings',
          component: () => import('@/views/admin/Settings.vue'),
          meta: { title: '系统设置' },
          children: [
            // 关于我们设置
            {
              path: 'about',
              name: 'AboutSettings',
              component: () => import('@/views/admin/settings/AboutSettings.vue'),
              meta: { requiresAuth: true, role: 'admin' }
            },
            // 业务范围设置
            {
              path: 'business-scope',
              name: 'BusinessScopeSettings',
              component: () => import('@/views/admin/settings/BusinessScopeSettings.vue'),
              meta: { requiresAuth: true, role: 'admin' }
            }
          ]
        },
        {
          path: 'categories',
          name: 'admin-categories',
          component: () => import('@/views/admin/CategoryManagement.vue'),
          meta: { title: '分类管理' }
        },
        {
          path: 'contact-messages',
          name: 'admin-contact-messages',
          component: () => import('@/views/admin/ContactMessages.vue'),
          meta: { title: '联系我们消息管理' }
        },
        {
          path: 'translations',
          name: 'admin-translations',
          component: () => import('@/views/admin/Translations.vue'),
          meta: { title: '翻译管理', requiresAuth: true, role: 'admin' }
        },
        {
          path: 'achievements',
          name: 'admin-achievements',
          component: () => import('@/views/admin/AchievementsAdmin.vue'),
          meta: { title: '业绩管理', requiresAuth: true }
        }
      ]
    },
    {
      path: '/:lang/login',
      name: 'login',
      component: () => import('@/views/Login.vue'),
      meta: { title: '登录' }
    },
    {
      path: '/:lang/404',
      name: '404',
      component: () => import('@/views/NotFound.vue'),
      meta: { title: '页面未找到' }
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/zh/404'
    }
  ]
})
// 
// 访问统计函数
function trackPageView(path: string) {
  // 忽略 API 路径和后台管理路径
  if (path.indexOf('/api/') === 0 || path.indexOf('/admin/') === 0) {
    return
  }
  
  // console.log('正在访问:', path)
  // 发送访问统计请求
  fetch('/api/visit-statistics/track', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ path: path })
  })
  .catch(function(error) {
    console.error('访问统计失败:', error)
  })
}

// Navigation guards
router.beforeEach(function(to, from, next) {
  var authStore = useAuthStore()
  
  console.log('=== 路由守卫：' + from.path + ' → ' + to.path + ' ===')
  // console.log('需要认证:', to.meta.requiresAuth)
  // console.log('认证状态:', authStore.isAuthenticated)
  // console.log('token 存在:', !!authStore.token)
  // console.log('用户存在:', !!authStore.user)
  
  // Update page title
  if (to.meta.title) {
    // 获取当前语言
    const currentLang = (to.params.lang as string) || 'zh'
    const langCode = currentLang === 'zh' ? 'zh-CN' : currentLang
    // console.log('当前语言:', langCode)
    // 后台管理和登录页面保持原标题
    if (to.path.includes('/api') || to.path.includes('/login')) {
      document.title = to.meta.title + ' - 安徽艾尔工程建设有限公司'
    } else {
      // 前台页面从导航菜单翻译中获取标题
      let pageTitle = to.meta.title as string
      
      // 根据路由的名称从 nav 翻译中获取对应语言的标题
      if (to.name) {
        const routeName = to.name.toString()
        // 处理项目详情和新闻详情等带参数的路由
        const baseRouteName = routeName.split('-')[0]
        
        try {
          // 尝试从 nav 翻译中获取标题（使用可选链避免报错）
          const navTitle = i18n.global.te(`nav.${baseRouteName}`, langCode) 
            ? i18n.global.t(`nav.${baseRouteName}`)
            : null
          if (navTitle && navTitle !== `nav.${baseRouteName}`) {
            pageTitle = navTitle
          }
        } catch (error) {
          // console.log('获取导航标题翻译失败:', error)
        }
      }
      
      // 获取公司名称翻译
      let companyName = '安徽艾尔工程建设有限公司'
      try {
        const translatedCompanyName = i18n.global.te('footer.company.name', langCode)
          ? i18n.global.t('footer.company.name')
          : '安徽艾尔工程建设有限公司'
        if (translatedCompanyName && translatedCompanyName !== 'footer.company.name') {
          companyName = translatedCompanyName
        }
      } catch (error) {
        // console.log('获取公司名称翻译失败:', error)
      }
      
      document.title = pageTitle + ' - ' + companyName
    }
  }
  
  // 跟踪页面访问
  trackPageView(to.path)
  
  // Check authentication for protected routes
  if (to.meta.requiresAuth) {
    // 如果有 token 但没有用户信息，尝试获取用户信息
    if (authStore.token && !authStore.user) {
      // console.log('有 token 但无用户信息，正在获取...')
      try {
        authStore.checkAuth().catch(function(error) {
          console.error('获取用户信息失败:', error)
        })
      } catch (error) {
        console.error('获取用户信息失败:', error)
      }
    }
    
    if (!authStore.isAuthenticated) {
      console.log('未认证，重定向到登录页')
      // 获取当前语言参数
      const lang = to.params.lang || 'zh'
      next(`/${lang}/login`)
    } else {
      // console.log('允许访问')
      next()
    }
  } else {
    // console.log('允许访问')
    next()
  }
})

export default router